import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Box } from "@mui/material"; // for layout + click target
import GlassContainer from "../../partners/components/liquid-glass";

export type MacroRingsWebProps = {
  caloriesToday: number;
  caloriesGoal: number;
  proteinToday: number;
  proteinGoal: number;
  carbsToday: number;
  carbsGoal: number;
  fatToday: number;
  fatGoal: number;

  /** Desktop/base sizing */
  size?: number; // SVG diameter (px)
  ringWidth?: number; // thickness per ring
  gap?: number; // gap between rings

  /** Mobile-friendly toggle (compact layout + responsive SVG) */
  mobile?: boolean;

  backgroundTrack?: string;
  bg?: string; // kept for backwards-compat; inner panel is transparent under glass
  textColor?: string;
  subTextColor?: string;
  title?: string;
  durationMs?: number; // fill duration per ring
  staggerMs?: number; // delay between rings
  isLightMode?: boolean; // light theme toggle
};

export type MacroRingsWebHandle = { reAnimate: () => void };

// --- easing + RAF tween ------------------------------------------------------
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

function rafTween({
  from,
  to,
  duration,
  delay = 0,
  easing = easeOutCubic,
  onUpdate,
  onComplete,
}: {
  from: number;
  to: number;
  duration: number;
  delay?: number;
  easing?: (t: number) => number;
  onUpdate: (v: number) => void;
  onComplete?: () => void;
}) {
  let raf = 0;
  const start = performance.now() + delay;

  const tick = (now: number) => {
    if (now < start) {
      raf = requestAnimationFrame(tick);
      return;
    }
    const t = Math.min(1, (now - start) / duration);
    onUpdate(from + (to - from) * easing(t));
    if (t < 1) raf = requestAnimationFrame(tick);
    else onComplete?.();
  };

  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}

// Animated progress as STATE (not ref) so React re-renders on each frame
function useRingProgress(
  target: number,
  duration: number,
  seed: number,
  delay = 0
) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    setValue(0);
    const stop = rafTween({
      from: 0,
      to: Math.max(0, Math.min(1, target)),
      duration,
      delay,
      onUpdate: setValue,
    });
    return stop;
  }, [target, duration, delay, seed]);
  return value;
}

const MacroRingsWeb = forwardRef<MacroRingsWebHandle, MacroRingsWebProps>(
  (
    {
      caloriesToday,
      caloriesGoal,
      proteinToday,
      proteinGoal,
      carbsToday,
      carbsGoal,
      fatToday,
      fatGoal,

      size = 220,
      ringWidth = 14,
      gap = 4,

      mobile = false,

      backgroundTrack,
      bg, // no longer used for container background; glass handles it
      textColor,
      subTextColor,

      title = "Food",
      durationMs = 900,
      staggerMs = 150,
      isLightMode = false,
    },
    ref
  ) => {
    // Theme defaults
    const DARK_DEFAULTS = {
      backgroundTrack: "#0f1f1c",
      textColor: "#e7f6f1",
      subTextColor: "#b8d8cf",
    };
    const LIGHT_DEFAULTS = {
      backgroundTrack: "white",
      textColor: "#0f1f1c",
      subTextColor: "#5b7d73",
    };
    const THEME = isLightMode ? LIGHT_DEFAULTS : DARK_DEFAULTS;

    const backgroundTrackC = backgroundTrack ?? THEME.backgroundTrack;
    const textColorC = textColor ?? THEME.textColor;
    const subTextColorC = subTextColor ?? THEME.subTextColor;

    const COLORS = {
      calories: "#127351", // outer
      protein: "#f44336",
      carbs: "#ffb300",
      fats: "#2196f3", // inner
    };

    const safeDiv = (num: number, den: number) => (den > 0 ? num / den : 0);

    const pCal = Math.min(1, safeDiv(caloriesToday, caloriesGoal));
    const pPro = Math.min(1, safeDiv(proteinToday, proteinGoal));
    const pCar = Math.min(1, safeDiv(carbsToday, carbsGoal));
    const pFat = Math.min(1, safeDiv(fatToday, fatGoal));

    // Mobile-friendly sizing
    const effectiveSize = mobile ? Math.min(200, size) : size;
    const effectiveRing = mobile ? Math.max(10, ringWidth - 3) : ringWidth;
    const effectiveGap = mobile ? Math.max(2, gap - 1) : gap;

    // seed forces re-run of animations
    const [seed, setSeed] = useState(0);
    const reAnimate = () => setSeed((s) => s + 1);
    useImperativeHandle(ref, () => ({ reAnimate }), []);

    const center = effectiveSize / 2;
    const r1 = Math.max(0, center - effectiveRing / 2);
    const r2 = Math.max(0, r1 - (effectiveRing + effectiveGap));
    const r3 = Math.max(0, r2 - (effectiveRing + effectiveGap));
    const r4 = Math.max(0, r3 - (effectiveRing + effectiveGap));
    const C1 = 2 * Math.PI * r1;
    const C2 = 2 * Math.PI * r2;
    const C3 = 2 * Math.PI * r3;
    const C4 = 2 * Math.PI * r4;

    // animated progresses (staggered)
    const a1 = useRingProgress(pCal, durationMs, seed, 0);
    const a2 = useRingProgress(pPro, durationMs, seed, staggerMs);
    const a3 = useRingProgress(pCar, durationMs, seed, staggerMs * 2);
    const a4 = useRingProgress(pFat, durationMs, seed, staggerMs * 3);

    // convert to dashoffsets
    const d1 = C1 * (1 - a1);
    const d2 = C2 * (1 - a2);
    const d3 = C3 * (1 - a3);
    const d4 = C4 * (1 - a4);

    const Round = (n: number) => Math.round(n);

    return (
      <Box
        sx={{
          // Use the glass component's flex container for layout
          flexDirection: mobile ? "column" : "row",
          alignItems: "center",
          gap: mobile ? 1.75 : 2,
          p: mobile ? 1.5 : 2,
          width: mobile ? "100%" : "fit-content",
          maxWidth: mobile ? 360 : "unset",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {/* We keep the click on a child Box to avoid modifying GlassContainer's API */}
        <Box
          onClick={reAnimate}
          sx={{
            display: "flex",
            flexDirection: mobile ? "column" : "row",
            alignItems: "center",
            gap: mobile ? 1.75 : 2,
            width: "100%",
          }}
          aria-label={`${title} rings summary (tap to replay animation)`}
          title="Tap to replay animation"
        >
          <svg
            // Responsive on mobile, fixed size on desktop
            width={mobile ? "100%" : effectiveSize}
            height={mobile ? "auto" : effectiveSize}
            viewBox={`0 0 ${effectiveSize} ${effectiveSize}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
              display: "block",
              maxWidth: mobile ? 240 : effectiveSize,
              margin: mobile ? "4px auto 0" : 0,
            }}
          >
            <g transform={`rotate(-90 ${center} ${center})`}>
              {/* tracks */}
              <circle
                cx={center}
                cy={center}
                r={r1}
                stroke={backgroundTrackC}
                strokeWidth={effectiveRing}
                fill="none"
              />
              <circle
                cx={center}
                cy={center}
                r={r2}
                stroke={backgroundTrackC}
                strokeWidth={effectiveRing}
                fill="none"
              />
              <circle
                cx={center}
                cy={center}
                r={r3}
                stroke={backgroundTrackC}
                strokeWidth={effectiveRing}
                fill="none"
              />
              <circle
                cx={center}
                cy={center}
                r={r4}
                stroke={backgroundTrackC}
                strokeWidth={effectiveRing}
                fill="none"
              />
              {/* progress */}
              <circle
                cx={center}
                cy={center}
                r={r1}
                stroke={COLORS.calories}
                strokeWidth={effectiveRing}
                strokeLinecap="round"
                fill="none"
                strokeDasharray={C1}
                strokeDashoffset={d1}
              />
              <circle
                cx={center}
                cy={center}
                r={r2}
                stroke={COLORS.protein}
                strokeWidth={effectiveRing}
                strokeLinecap="round"
                fill="none"
                strokeDasharray={C2}
                strokeDashoffset={d2}
              />
              <circle
                cx={center}
                cy={center}
                r={r3}
                stroke={COLORS.carbs}
                strokeWidth={effectiveRing}
                strokeLinecap="round"
                fill="none"
                strokeDasharray={C3}
                strokeDashoffset={d3}
              />
              <circle
                cx={center}
                cy={center}
                r={r4}
                stroke={COLORS.fats}
                strokeWidth={effectiveRing}
                strokeLinecap="round"
                fill="none"
                strokeDasharray={C4}
                strokeDashoffset={d4}
              />
            </g>
          </svg>

          {/* values */}
          <div
            style={{
              minWidth: mobile ? "auto" : 160,
              width: mobile ? "100%" : "auto",
              paddingBottom: mobile ? 6 : 0,
              background: "transparent",
            }}
          >
            <Row
              dotColor={COLORS.calories}
              label={`Calories - ${Round(caloriesGoal)}`}
              main={`${Round(caloriesToday)}`}
              unit=""
              textColor={textColorC || "#fff"}
              subTextColor={subTextColorC || "#cbd5df"}
              small={mobile}
              mobile={mobile}
            />
            <Row
              dotColor={COLORS.protein}
              label={`Protein - ${Round(proteinGoal)}`}
              main={`${Round(proteinToday)} g`}
              textColor={textColorC || "#fff"}
              subTextColor={subTextColorC || "#cbd5df"}
              small={mobile}
              unit=""
              mobile={mobile}
            />
            <Row
              dotColor={COLORS.carbs}
              label={`Carbs - ${Round(carbsGoal)}`}
              main={`${Round(carbsToday)} g`}
              textColor={textColorC || "#fff"}
              subTextColor={subTextColorC || "#cbd5df"}
              small={mobile}
              unit=""
              mobile={mobile}
            />
            <Row
              dotColor={COLORS.fats}
              label={`Fats - ${Round(fatGoal)}`}
              main={`${Round(fatToday)} g`}
              textColor={textColorC || "#fff"}
              subTextColor={subTextColorC || "#cbd5df"}
              small={mobile}
              unit=""
              mobile={mobile}
            />
          </div>
        </Box>
      </Box>
    );
  }
);

function Row({
  dotColor,
  label,
  main,
  unit,
  textColor,
  subTextColor,
  small = false,
  mobile = false,
}: {
  dotColor: string;
  label: string;
  main: string;
  unit?: string;
  textColor?: string;
  subTextColor?: string;
  small?: boolean;
  mobile?: boolean;
}) {
  if (mobile) {
    // --- mobile: inline label + value ---
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          columnGap: 12,
          marginBottom: small ? 8 : 12,
          width: "100%",
        }}
      >
        {/* Left: dot + label */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
        >
          <span
            style={{
              width: small ? 8 : 10,
              height: small ? 8 : 10,
              borderRadius: 999,
              background: dotColor,
              flex: "0 0 auto",
            }}
          />
          <span
            style={{
              color: subTextColor,
              fontSize: small ? 12 : 13,
              fontWeight: 600,
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </span>
        </div>

        {/* Right: value */}
        <div
          style={{
            color: textColor,
            fontSize: small ? 22 : 26,
            fontWeight: 800,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            textAlign: "right",
          }}
        >
          {main} {unit ?? ""}
        </div>
      </div>
    );
  }

  // --- desktop: stacked value below label ---
  return (
    <div style={{ marginBottom: small ? 8 : 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: small ? 8 : 10,
            height: small ? 8 : 10,
            borderRadius: 999,
            background: dotColor,
            display: "inline-block",
          }}
        />
        <span
          style={{
            color: subTextColor,
            fontSize: small ? 12 : 13,
            fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          color: textColor,
          fontSize: small ? 24 : 28,
          fontWeight: 800,
          lineHeight: 1.1,
          marginLeft: 18,
          marginTop: 2,
        }}
      >
        {main} {unit ?? ""}
      </div>
    </div>
  );
}

export default MacroRingsWeb;
